import { Services, ReadCallback, InitOptions } from 'i18next';
import { arrify, delay } from '@djs';
import { RawResourceLanguage, formatResourceLanguage } from '../utils/resourceLanguage';
import { RequiredFormatKeyPaths } from '../utils/formatKeysDefaultPaths';
import config from '../config.json';

export type BackendFallback = (
  lng: string,
  refetch: (lng: string) => Promise<RawResourceLanguage>,
) => Promise<RawResourceLanguage>;

const RETRY_FETCH_DELAY = 400;

const DEFAULT_SOURCE_URL = config.defaultSourceUrl;

export interface BackendOptions {
  /* Ключ проекта, по которому доступны нужные переводы из платформы переводов ВК */
  name: string;
  /* Основной префикс ключей переводов, относящихся к проекту */
  defaultNsPrefix: string;
  /**
   * Источник получения переводов (по умолчанию: url)
   * - url - по внешней ссылке (см. параметр sourceUrl)
   * - fallback - альтернативным способом с помощью fallback (необходим параметр fallback)
   */
  source?: 'url' | 'fallback';
  /**
   * Внешняя ссылка платформы переводов (доступна интерполяция {name} и {lang})
   * По умолчанию: https://vk.com/js/lang-pack.js?name={name}&lang={lang}&format=json
   */
  sourceUrl?: string;
  /**
   * Альтернатинвный способ получения данных, применяемый
   * в случае ошибки получения переводов ВК или при указанном source = 'fallback'
   */
  fallback?: BackendFallback;
  /**
   * Переопределение путей по-умолчанию к ключам, необходимым для форматирования дат, чисел и строк
   */
  formatKeyPaths?: Partial<RequiredFormatKeyPaths>;
}

export function validateBackendOptions(options: any): options is BackendOptions {
  return Boolean(
    options &&
    typeof options === 'object' &&
    typeof options.name === 'string' &&
    typeof options.defaultNsPrefix === 'string' &&
    (typeof options.source === 'undefined' || ['url', 'fallback'].includes(options.source)) &&
    (typeof options.sourceUrl === 'undefined' || typeof options.sourceUrl === 'string') &&
    ['function', 'undefined'].includes(typeof options.fallback) &&
    (options.source !== 'fallback' || typeof options.fallback === 'function') &&
    (typeof options.formatKeyPaths === 'undefined' ||
      (options.formatKeyPaths && typeof options.formatKeyPaths === 'object')),
  );
}

class BackendError extends Error {}

/**
 * Модуль подгрузки словаря переводов ВК
 */
class Backend {
  static readonly type = 'backend';
  readonly type = 'backend';
  private static sexModuleConnected = false;

  static readonly DEFAULT_SOURCE = 'url';

  backendOptions: BackendOptions = {
    name: 'name',
    defaultNsPrefix: 'name',
  };

  services = {} as Services;

  defaultNs = 'translation'; // нэмспейс i18next по умолчанию

  /**
   * Инициализация модуля.
   * Выполняет проверку необходимых полей в backendOptions для работы модуля
   * и устанавливает необходимые параметры инициализации i18next
   */
  init(services: Services, backendOptions: any = {}, i18nextOptions: InitOptions) {
    if (validateBackendOptions(backendOptions)) {
      this.backendOptions = backendOptions;
    } else {
      throw new BackendError('Объект backend не соответствует интерфейсу BackendOptions');
    }

    this.services = services;

    services.resourceStore.options.fallbackLng = false;

    services.resourceStore.options.interpolation = {
      ...services.resourceStore.options.interpolation,
      prefix: '{',
      suffix: '}',
    };

    services.interpolator.reset();

    this.defaultNs = arrify(i18nextOptions.defaultNS || this.defaultNs)[0];
  }

  /**
   * Подгружает словарь переводов ВК и преобразует его для i18next
   */
  read(lng: string, namespace: string, callback: ReadCallback) {
    const { fallback, name, source, sourceUrl = DEFAULT_SOURCE_URL } = this.backendOptions;

    const nameLngSourceUrl = sourceUrl
      .replace('{name}', encodeURIComponent(name))
      .replace('{lang}', encodeURIComponent(lng));

    const customFallback = () => fallback && fallback(lng, () => Backend.fetchRawResource(nameLngSourceUrl));

    const getRawResourceLanguage = {
      url() {
        return Backend.fetchRawResource(nameLngSourceUrl).catch((e) => customFallback() || Promise.reject(e));
      },
      fallback() {
        return customFallback() || Promise.reject(new BackendError('Поле fallback у объекта backend не было получено'));
      },
    };

    getRawResourceLanguage[source || Backend.DEFAULT_SOURCE]()
      .then((rawResourceLanguage) => {
        const resource = formatResourceLanguage(
          rawResourceLanguage,
          { ...config.formatKeysDefaultPaths, ...this.backendOptions.formatKeyPaths },
          this.backendOptions.defaultNsPrefix,
          this.defaultNs,
        );

        this.services.resourceStore.data[lng] = resource;

        callback(null, resource[namespace]);
      })
      .catch(() => {
        callback(new BackendError('Ошибка загрузки словаря через платформу переводов ВК'), false);
      });
  }

  /**
   * Скачивает словарь в платформе переводов ВК
   * @param url путь к словарю переводов
   */
  static fetchRawResource(url: string): Promise<RawResourceLanguage> {
    return fetch(url)
      .catch(() => delay(RETRY_FETCH_DELAY).then(() => fetch(url)))
      .then((response) => response.json());
  }

  create() {
    // nothing, but required by BackendModule interface
  }
}

export { Backend };
